import React, { memo, useEffect, useRef } from 'react'
import Viewer from '@toast-ui/editor/dist/toastui-editor-viewer'
import '@toast-ui/editor/dist/toastui-editor-viewer.css'

/**
 *
 * // 基础使用
 * <ToastViewer value="# Hello World\n这是一段 **粗体** 文本" />
 *
 * // 带主题切换
 * const [theme, setTheme] = useState<'light' | 'dark'>('light');
 * <ToastViewer
 * 	value={markdownContent}
 * 	theme={theme}
 * 	className="my-custom-viewer"
 * />
 *
 * // 在博客文章页面中使用
 * const BlogPost = ({ post }) => (
 * 	<article>
 * 		<h1>{post.title}</h1>
 * 		<ToastViewer value={post.content} />
 * 	</article>
 * );
 */
interface ToastViewerProps {
	value: string
	className?: string
	theme?: 'light' | 'dark'
}

const OneEditorViewer: React.FC<ToastViewerProps> = ({ value, className, theme = 'light' }) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const viewerRef = useRef<Viewer | null>(null)

	// 🚀 初始化渲染器
	useEffect(() => {
		if (containerRef.current) {
			viewerRef.current = new Viewer({
				el: containerRef.current,
				initialValue: value,
				theme: theme
			})
		}

		// 🧹 清理工作
		return () => {
			if (viewerRef.current) {
				viewerRef.current?.destroy()
			}
		}
	}, []) // 只在挂载时执行

	// 🔄 响应内容变化
	useEffect(() => {
		if (viewerRef.current && value !== undefined) {
			viewerRef.current?.setMarkdown(value)
		}
	}, [value])

	return <div ref={containerRef} className={`toast-viewer ${className || ''}`} />
}

// 🚀 使用 memo 优化性能，避免不必要的重渲染
const ViewerComponentWrapper = memo(OneEditorViewer)
export default ViewerComponentWrapper
